import { CheckCircleIcon, ClockIcon, ExclamationCircleIcon, NoSymbolIcon } from '@heroicons/react/24/outline';
import { toast as sonnerToast } from 'sonner';
import { isMFARejectedCodeError } from 'lib/constants';
import { captureError } from 'utils/datadog-error';
import { isExpiredSessionError } from 'utils/error';

const baseClassName = 'text-sm font-medium !text-white !rounded-md !shadow-xl !px-4.5 !py-2.5 min-w-min';
const successClassName = `!bg-green-600 ${baseClassName}`;
const errorClassName = `!bg-therared ${baseClassName}`;
const warningClassName = `!bg-amber-700 ${baseClassName}`;
const infoClassName = `!bg-therablue ${baseClassName}`;

interface ToastOptions {
  id?: string;
  duration?: number;
  icon?: JSX.Element | string | null;
  className?: string;
  /** Additional context data to include with the error */
  data?: any;
  /** Send to Slack (default: true for errors) */
  sendToSlack?: boolean;
  /** Datadog grouping key when errors sharing one on-screen toast `id` should land in separate issues */
  fingerprint?: string;
}

type ToastMessage = JSX.Element | string;

/** Error object type that can be either an Error instance or a plain object (e.g., API response) */
type ErrorInput = Error | Record<string, any>;

/**
 * Build a stable Error Tracking grouping key for a toast.
 *
 * Every toast error is created via `new Error(message)` here, so they share one stack signature
 * and would otherwise collapse into a single Datadog issue. The toast `id` (e.g.
 * 'failed-to-approve-invoices') is the most reliable per-feature key; fall back to the message with
 * dynamic parts (UUIDs, numbers) stripped so variable copy doesn't fragment groups.
 */
const buildToastFingerprint = (id: string | undefined, message: ToastMessage): string => {
  if (id) return `toast:${id}`;
  if (typeof message === 'string') {
    const normalized = message
      .toLowerCase()
      .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/g, '')
      .replace(/\d+/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    if (normalized) return `toast:${normalized}`;
  }
  return 'toast:unknown';
};

// 2FA failures arrive as StytchAPIError, which the Datadog filter drops by error name along with
// the rest of the Stytch noise. Re-wrapping these few types keeps them visible without lifting
// the filter for everything else.
const MFA_ERROR_TYPES = [
  'unable_to_authenticate_totp',
  'unable_to_auth_otp_code',
  'totps_not_found_for_user',
  'totp_not_found',
  'expired_totp',
];

const theraToast = {
  success: (message: ToastMessage, options?: ToastOptions) => {
    return sonnerToast.success(message, {
      icon: options?.icon ?? <CheckCircleIcon className="h-6 w-6 min-w-[2rem]" />,
      className: `${successClassName} ${options?.className ?? ''}`,
      ...(options?.id && { id: options.id }),
      ...(options?.duration && { duration: options.duration }),
    });
  },

  /**
   * Show error toast and log to Datadog.
   *
   * @example
   * // Simple message
   * theraToast.error({ message: 'Something went wrong' });
   *
   * // With Error instance (preserves stack trace)
   * theraToast.error({ message: 'Payment failed', error });
   *
   * // With API response object (attached as context)
   * theraToast.error({ message: 'Payment failed', error: apiResponse });
   *
   * // With options
   * theraToast.error({ message: 'Payment failed', error, options: { id: 'payment-error' } });
   */
  error: ({
    message,
    error,
    options,
  }: {
    message: ToastMessage;
    error?: ErrorInput;
    options?: ToastOptions;
  }): string | number | undefined => {
    if (!message || message === 'UnAuthorized' || message === 'Request_Cancelled') {
      if (options?.id) sonnerToast.dismiss(options.id);
      return undefined;
    }

    // Prepare tags for Datadog
    const tags: Record<string, string> = {};
    if (options?.sendToSlack !== false) {
      tags.thera_error_type = 'toast_error';
      tags.user_facing = 'true';
      tags.send_to_slack = 'true';
    }

    // Expired sessions are handled by the sign-in redirect, so they need neither a toast nor a
    // Datadog entry. Permission denials share the 401 status but must still be shown; swallowing
    // them left the user staring at a failed action with no explanation at all.
    //
    // Exception: Stytch also returns 401 when the user simply typed the wrong 2FA code. That's a
    // user-facing "your code is wrong" error, not a session expiry, so it must still show its
    // toast rather than being silently dismissed.
    const errorAny = error as any;
    const mfaErrorType = MFA_ERROR_TYPES.includes(errorAny?.error_type) ? errorAny.error_type : undefined;
    if (!isMFARejectedCodeError(errorAny?.error_type) && isExpiredSessionError(errorAny)) {
      if (options?.id) sonnerToast.dismiss(options.id);
      return undefined;
    }

    // Determine what to send to Datadog
    let errorToLog: Error;
    let contextData = options?.data;

    if (mfaErrorType) {
      errorToLog = new Error(`2FA verification failed: ${mfaErrorType}`);
      contextData = { ...contextData, originalError: error };
    } else if (error instanceof Error) {
      errorToLog = error;
    } else if (error) {
      errorToLog = new Error(typeof message === 'string' ? message : 'Unknown error');
      contextData = { ...contextData, originalError: error };
    } else {
      errorToLog = new Error(typeof message === 'string' ? message : 'Unknown error');
    }

    captureError(errorToLog, {
      source: 'TheraToast',
      data: contextData,
      isToastError: options?.sendToSlack === false ? false : true,
      tags,
      fingerprint: buildToastFingerprint(options?.fingerprint ?? options?.id, message),
    });

    return sonnerToast.error(message, {
      icon: options?.icon ?? <NoSymbolIcon className="h-6 w-6 min-w-[2rem]" />,
      className: `${errorClassName} ${options?.className ?? ''}`,
      ...(options?.id && { id: options.id }),
      ...(options?.duration && { duration: options.duration }),
    });
  },

  warning: (message: ToastMessage, options?: ToastOptions) => {
    return sonnerToast.error(message, {
      icon: options?.icon ?? <ExclamationCircleIcon className="h-6 w-6 min-w-[2rem]" />,
      className: `${warningClassName} ${options?.className ?? ''}`,
      ...(options?.id && { id: options.id }),
      ...(options?.duration && { duration: options.duration }),
    });
  },

  info: (message: ToastMessage, options?: ToastOptions) => {
    return sonnerToast.success(message, {
      icon: options?.icon ?? <ClockIcon className="h-6 w-6 min-w-[2rem]" />,
      className: `${infoClassName} ${options?.className ?? ''}`,
      ...(options?.id && { id: options.id }),
      ...(options?.duration && { duration: options.duration }),
    });
  },

  dismiss: (id?: string | number) => {
    return sonnerToast.dismiss(id);
  },
};

/**
 * Mercoa-compatible toast wrapper for use with @mercoa/react components.
 * This adapts our theraToast interface to match Mercoa's ToastClient interface.
 */
export const mercoaToast = {
  success: (message: string) => theraToast.success(message),
  error: (message: string) => theraToast.error({ message }),
  info: (message: string) => theraToast.info(message),
};

export default theraToast;
